import { Button, Dialog, Loading } from '@/components';
import React, { Fragment, ReactNode } from 'react';
import { Card, Col, Container, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'react-feather';
import { colors } from '@/styles/colors';
import { CustomTable } from '@/components/Table';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { toPercentage } from '@/helpers/common/amount';
import ProducerReportType from '@/model/ProducerReportType';
import { ActionProps } from '@/components/Dialog';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import Contractor from '@/model/Contractor';
import { EventMoneyTransfer } from '@/model/EventMoneyTransfer';
import dayjs from 'dayjs';
import EventMoneyTransferStatus from '@/model/EventMoneyTransferStatus';
import { ProducerReportMain } from '@/model/ProducerReportMain';
import { ReactComponent as Detail } from '@/assets/images/svg/detail.svg';
import { ReactComponent as Download } from '@/assets/images/svg/download.svg';
import { unmask } from '@/helpers/masks/cashNumber';
import Bank from '@/model/Bank';
import { FormInput, ShouldShowModal, States, transferColumns } from '../../types';
import { TransferContent } from '../../components/TransferContent';
import { DetailContent } from '../../components/DetailContent';
import { PixContent } from '../../components/PixContent';

interface StateProps {
  state: States;
  producerReportMain?: ProducerReportMain;
  title: string | ReactNode;
  visible: boolean;
  shouldShowModal: ShouldShowModal;
  formData: FormData;
  formErrors: FormErrors;
  contractors: Contractor[];
  eventMoneyTransfer?: EventMoneyTransfer;
  banks: Bank[];
  formDataPix: FormData;
  formErrorsPix: FormErrors;
}

interface DispatchProps {
  onToggle: () => void;
  onChangeFormInput: OnChangeFormInput;
  onTransfer: () => void;
  onAdvance: () => void;
  onShouldShowModal: ({
    value,
    newTitleModal,
    selectedId,
    notToogle,
  }: {
    value: ShouldShowModal;
    newTitleModal: string | React.ReactNode;
    selectedId?: string;
    notToogle?: boolean;
  }) => void;
  onDownload: (itemUrl: string) => void;
  onClosePix: () => void;
  onChangeFormInputPix: OnChangeFormInput;
  onSavePix: () => void;
}

type Props = StateProps & DispatchProps;

export const ProducerReportContainer: React.FC<Props> = ({
  state,
  producerReportMain,
  title,
  visible,
  onToggle,
  shouldShowModal,
  onTransfer,
  onAdvance,
  formData,
  formErrors,
  contractors,
  onChangeFormInput,
  onShouldShowModal,
  eventMoneyTransfer,
  onDownload,
  onClosePix,
  banks,
  formDataPix,
  formErrorsPix,
  onChangeFormInputPix,
  onSavePix,
}): JSX.Element => {
  const checkIfIsAdvance = (): boolean => {
    let isAdvance = false;
    if (formData[FormInput.contractor] && contractors && contractors.length > 0) {
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < contractors.length; i++) {
        if (contractors[i].id === formData[FormInput.contractor]) {
          if (contractors[i].advance) {
            if (formData[FormInput.transferValue]) {
              if (producerReportMain && producerReportMain.summary) {
                const transferValue = Number(unmask(formData[FormInput.transferValue]));
                if (transferValue > producerReportMain.summary.balance) {
                  isAdvance = true;
                }
              }
            }
          }
          break;
        }
      }
    }
    return isAdvance;
  };

  const renderActionDialogToCancel: ActionProps = {
    title: 'Cancelar',
    onClick: (): void => onToggle(),
    theme: 'noneBorder',
  };

  const renderActionDialogToCancelPix: ActionProps = {
    title: 'Cancelar',
    onClick: (): void => onClosePix(),
    theme: 'noneBorder',
  };

  const getTransferStatus = (status: EventMoneyTransferStatus): string => {
    let value = '';
    if (status === EventMoneyTransferStatus.WAITING) {
      value = 'Aguardando';
    } else if (status === EventMoneyTransferStatus.TRANSFERRED) {
      value = 'Transferindo';
    } else if (status === EventMoneyTransferStatus.REFUSED) {
      value = 'Recusado';
    }
    return value;
  };

  const getTransferDate = (transfer: EventMoneyTransfer): string => {
    let date = '---';
    if (transfer.transferStatus === EventMoneyTransferStatus.TRANSFERRED && transfer.transferDate) {
      date = dayjs(transfer.transferDate).format('DD/MM/YYYY HH:mm:ss');
    } else if (
      transfer.transferStatus === EventMoneyTransferStatus.REFUSED &&
      transfer.refusedDate
    ) {
      date = dayjs(transfer.refusedDate).format('DD/MM/YYYY HH:mm:ss');
    }
    return date;
  };

  const dataTableTransfers =
    producerReportMain &&
    producerReportMain.waitingTransfers &&
    producerReportMain.waitingTransfers.length > 0
      ? producerReportMain.waitingTransfers.map(item => ({
          contractor: item.contractor.name,
          bank: item.pixKey.bank.fullName,
          pixKey: item.pixKey.key,
          contractorUser: item.contractorUser.name,
          transferValue: toCurrency(item.transferValue),
          transferRequestDate: dayjs(item.transferRequestDate).format('DD/MM/YYYY HH:mm:ss'),
          transferred: getTransferStatus(item.transferStatus),
          transferDate: getTransferDate(item),
          action: (
            <React.Fragment>
              {((item.transferStatus === EventMoneyTransferStatus.TRANSFERRED &&
                item.transferDate) ||
                (item.transferStatus === EventMoneyTransferStatus.REFUSED && item.refusedDate)) && (
                <Detail
                  className={'mr-4 svg-icon action-icon'}
                  onClick={() =>
                    onShouldShowModal({
                      value: ShouldShowModal.detail,
                      newTitleModal: 'Pedido',
                      selectedId: item.id,
                    })
                  }
                />
              )}
              {item.transferStatus === EventMoneyTransferStatus.TRANSFERRED &&
                item.transferDate &&
                item.attachmentUrl && (
                  <Download
                    title="Resgatar"
                    className={'mr-4 svg-icon-error action-icon'}
                    onClick={() => onDownload(item.attachmentUrl as string)}
                  />
                )}
            </React.Fragment>
          ),
        }))
      : [];

  const dataTableTransferredTransfers =
    producerReportMain &&
    producerReportMain.transferredTransfers &&
    producerReportMain.transferredTransfers.length > 0
      ? producerReportMain.transferredTransfers.map(item => ({
          contractor: item.contractor.name,
          bank: item.pixKey.bank.fullName,
          pixKey: item.pixKey.key,
          contractorUser: item.contractorUser.name,
          transferValue: toCurrency(item.transferValue),
          transferRequestDate: dayjs(item.transferRequestDate).format('DD/MM/YYYY HH:mm:ss'),
          transferred: getTransferStatus(item.transferStatus),
          transferDate: getTransferDate(item),
          action: (
            <React.Fragment>
              {((item.transferStatus === EventMoneyTransferStatus.TRANSFERRED &&
                item.transferDate) ||
                (item.transferStatus === EventMoneyTransferStatus.REFUSED && item.refusedDate)) && (
                <Detail
                  className={'mr-4 svg-icon action-icon'}
                  onClick={() =>
                    onShouldShowModal({
                      value: ShouldShowModal.detail,
                      newTitleModal: 'Pedido',
                      selectedId: item.id,
                    })
                  }
                />
              )}
              {item.transferStatus === EventMoneyTransferStatus.TRANSFERRED &&
                item.transferDate &&
                item.attachmentUrl && (
                  <Download
                    title="Resgatar"
                    className={'mr-4 svg-icon-error action-icon'}
                    onClick={() => onDownload(item.attachmentUrl as string)}
                  />
                )}
            </React.Fragment>
          ),
        }))
      : [];

  const dataTablerefRefusedTransfers =
    producerReportMain &&
    producerReportMain.refusedTransfers &&
    producerReportMain.refusedTransfers.length > 0
      ? producerReportMain.refusedTransfers.map(item => ({
          contractor: item.contractor.name,
          bank: item.pixKey.bank.fullName,
          pixKey: item.pixKey.key,
          contractorUser: item.contractorUser.name,
          transferValue: toCurrency(item.transferValue),
          transferRequestDate: dayjs(item.transferRequestDate).format('DD/MM/YYYY HH:mm:ss'),
          transferred: getTransferStatus(item.transferStatus),
          transferDate: getTransferDate(item),
          action: (
            <React.Fragment>
              {((item.transferStatus === EventMoneyTransferStatus.TRANSFERRED &&
                item.transferDate) ||
                (item.transferStatus === EventMoneyTransferStatus.REFUSED && item.refusedDate)) && (
                <Detail
                  className={'mr-4 svg-icon action-icon'}
                  onClick={() =>
                    onShouldShowModal({
                      value: ShouldShowModal.detail,
                      newTitleModal: 'Pedido',
                      selectedId: item.id,
                    })
                  }
                />
              )}
              {item.transferStatus === EventMoneyTransferStatus.TRANSFERRED &&
                item.transferDate &&
                item.attachmentUrl && (
                  <Download
                    title="Resgatar"
                    className={'mr-4 svg-icon-error action-icon'}
                    onClick={() => onDownload(item.attachmentUrl as string)}
                  />
                )}
            </React.Fragment>
          ),
        }))
      : [];

  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Dialog
        title={title}
        visible={visible}
        onClose={onToggle}
        position={'center'}
        isContentWithCard={true}
        actions={[
          {
            [ShouldShowModal.transfer]: renderActionDialogToCancel,
            [ShouldShowModal.detail]: renderActionDialogToCancel,
            [ShouldShowModal.pix]: renderActionDialogToCancelPix,
          }[shouldShowModal],
          {
            [ShouldShowModal.transfer]: {
              title: checkIfIsAdvance() ? 'Solicitar Advance' : 'Solicitar transferência',
              onClick: () => (checkIfIsAdvance() ? onAdvance() : onTransfer()),
              disabled: Object.keys(formErrors).length === 0 && formErrors.constructor === Object,
            },
            [ShouldShowModal.detail]: {},
            [ShouldShowModal.pix]: {
              title: 'Salvar',
              onClick: () => onSavePix(),
            },
          }[shouldShowModal],
        ]}
      >
        {
          {
            [ShouldShowModal.transfer]: (
              <TransferContent
                formData={formData}
                formErrors={formErrors}
                contractors={contractors}
                onChangeFormInput={onChangeFormInput}
                onShouldShowModal={onShouldShowModal}
              />
            ),
            [ShouldShowModal.detail]: <DetailContent data={eventMoneyTransfer} />,
            [ShouldShowModal.pix]: (
              <PixContent
                banks={banks}
                formData={formDataPix}
                formErrors={formErrorsPix}
                onChange={onChangeFormInputPix}
              />
            ),
          }[shouldShowModal]
        }
      </Dialog>
      <Container className="mainContainer" fluid={true}>
        <div className="d-flex justify-content-between mb-5">
          <div className="pageTitle d-flex">
            <Link to={`${process.env.PUBLIC_URL}/dashboard`}>
              <ArrowLeft color={colors.black} className="arrow-left" />
            </Link>
            <span className="ml-3 mb-0 mt-2">Lançamentos</span>
          </div>
          <Button
            title="Resgatar valor"
            onClick={(): void =>
              onShouldShowModal({
                value: ShouldShowModal.transfer,
                newTitleModal: 'Resgatar valor',
              })
            }
          />
        </div>
        <h5>Totalizadores</h5>
        {producerReportMain && producerReportMain.summary && (
          <Fragment>
            <div
              style={{
                border: '1px solid #ccc',
                borderRadius: '10px',
                padding: '20px 10px 10px 10px',
                display: 'inline-block',
                width: '100%',
                maxWidth: '600px',
                marginBottom: '20px',
                background: '#fff',
                boxShadow: '4px 4px 10px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Row
                className="mb-4"
                style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
              >
                <Col md={6}>
                  <div className="text-darkgray-regular">Valor total arrecadado:</div>
                </Col>
                <Col xs={6} style={{ textAlign: 'right' }}>
                  <div className="text-success">
                    {toCurrency(producerReportMain.summary.totalSale)}
                  </div>
                </Col>
              </Row>
              <Row
                className="mb-4"
                style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
              >
                <Col xs={6}>
                  <div className="text-darkgray-regular">Dinheiro:</div>
                </Col>
                <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                  <div className="text-danger">{toCurrency(producerReportMain.summary.money)}</div>
                </Col>
              </Row>
              <Row
                className="mb-4"
                style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
              >
                <Col xs={6}>
                  <div className="text-darkgray-regular">Vouchers/Descontos:</div>
                </Col>
                <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                  <div className="text-danger">
                    {toCurrency(producerReportMain.summary.vouchers)}
                  </div>
                </Col>
              </Row>
              <Row
                className="mb-4"
                style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
              >
                <Col xs={6}>
                  <div className="text-darkgray-regular">Despesas:</div>
                </Col>
                <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                  <div className="text-danger">
                    {toCurrency(producerReportMain.summary.discounts)}
                  </div>
                </Col>
              </Row>
              <Row
                className="mb-4"
                style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
              >
                <Col xs={6}>
                  <div className="text-darkgray-regular">Resgates:</div>
                </Col>
                <Col xs={6} style={{ textAlign: 'right', borderBottom: '1px' }}>
                  <div className="text-danger">
                    {toCurrency(producerReportMain.summary.transfers)}
                  </div>
                </Col>
              </Row>
              <Row
                className="mb-4"
                // style={{ borderBottom: '1px solid #ccc', paddingBottom: '5px' }}
              >
                <Col xs={6}>
                  <div
                    className="text-darkgray-regular"
                    style={{ fontWeight: 'bold', letterSpacing: '1px' }}
                  >
                    Saldo:
                  </div>
                </Col>
                <Col
                  xs={6}
                  style={{
                    textAlign: 'right',
                    borderBottom: '1px',
                    fontWeight: 'bold',
                    letterSpacing: '1px',
                  }}
                >
                  <div
                    className={
                      producerReportMain.summary.balance > 0 ? 'text-success' : 'text-danger'
                    }
                  >
                    {toCurrency(producerReportMain.summary.balance)}
                  </div>
                </Col>
              </Row>
            </div>
          </Fragment>
        )}
        {producerReportMain && (
          <Fragment>
            <h6 className="mb-4 collapseTableText">Receitas</h6>
            {producerReportMain.ticket &&
              producerReportMain.ticket.incomes &&
              producerReportMain.ticket.incomes.length > 0 && (
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <div className="p-4">
                    <div className="collapseTableText subTitleMain">
                      <label>Ingressos</label>
                    </div>
                    <CustomTable
                      theme="producerTheme"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={[
                        {
                          name: 'Descrição',
                          selector: row => row.description,
                        },
                        {
                          name: 'Valor',
                          selector: row => row.baseValue,
                        },
                      ]}
                      data={producerReportMain.ticket.incomes.map(item => ({
                        description: item.description,
                        baseValue: (
                          <label
                            style={{
                              color: item.type === ProducerReportType.DEBIT ? '#e64f49' : '#266f2c',
                            }}
                          >
                            {toCurrency(item.baseValue)}
                          </label>
                        ),
                      }))}
                    />
                  </div>
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Total:</div>
                        <div className="text-small-black-600">{`${toCurrency(
                          producerReportMain.ticket.summary.totalSale,
                        )}`}</div>
                      </div>
                    </>
                  </div>
                </Card>
              )}

            {producerReportMain.product &&
              producerReportMain.product.incomes &&
              producerReportMain.product.incomes.length > 0 && (
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <div className="p-4">
                    <div className="collapseTableText subTitleMain">
                      <label>Bar</label>
                    </div>
                    <CustomTable
                      theme="producerTheme"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={[
                        {
                          name: 'Descrição',
                          selector: row => row.description,
                        },
                        {
                          name: 'Valor',
                          selector: row => row.baseValue,
                        },
                      ]}
                      data={producerReportMain.product.incomes.map(item => ({
                        description: item.description,
                        baseValue: (
                          <label
                            style={{
                              color: item.type === ProducerReportType.DEBIT ? '#e64f49' : '#266f2c',
                            }}
                          >
                            {toCurrency(item.baseValue)}
                          </label>
                        ),
                      }))}
                    />
                  </div>
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Total:</div>
                        <div className="text-small-black-600">{`${toCurrency(
                          producerReportMain.product.summary.totalSale,
                        )}`}</div>
                      </div>
                    </>
                  </div>
                </Card>
              )}
            <hr className="dividerUp" />
            <h6 className="mb-4 collapseTableText">Despesas</h6>
            {producerReportMain.ticket &&
              producerReportMain.ticket.expenses &&
              producerReportMain.ticket.expenses.length > 0 && (
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <div className="p-4">
                    <div className="collapseTableText subTitleMain">
                      <label>Ingresso</label>
                    </div>
                    <CustomTable
                      theme="producerTheme"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={[
                        {
                          name: 'Descrição',
                          selector: row => row.description,
                        },
                        {
                          name: 'Valor base',
                          selector: row => row.baseValue,
                        },
                        {
                          name: 'Taxa',
                          selector: row => row.fee,
                        },
                        {
                          name: 'Valor',
                          selector: row => row.value,
                        },
                      ]}
                      data={producerReportMain.ticket.expenses.map(item => ({
                        description: item.description,
                        baseValue:
                          item.description === 'Emisao de cortesia' ||
                          item.description === 'Cancelamentos de ingresso' ||
                          item.description === 'Venda em dinheiro ADM/Dashboard' ||
                          item.description === 'Bloqueio de mesa ou cadeira' ||
                          item.description === 'Desbloqueio de mesa ou cadeira' ||
                          item.description === 'Boleto' ||
                          item.description === 'Aluguel equipamento'
                            ? // eslint-disable-next-line radix
                              parseInt(`${item.baseValue}`)
                            : toCurrency(item.baseValue),
                        // eslint-disable-next-line no-nested-ternary
                        fee: item.fee
                          ? item.description === 'Emisao de cortesia' ||
                            item.description === 'Cancelamentos de ingresso' ||
                            item.description === 'Venda em dinheiro ADM/Dashboard' ||
                            item.description === 'Bloqueio de mesa ou cadeira' ||
                            item.description === 'Desbloqueio de mesa ou cadeira' ||
                            item.description === 'Boleto' ||
                            item.description === 'Aluguel equipamento'
                            ? toCurrency(item.fee)
                            : `${toPercentage(item.fee)} %`
                          : 'N/A',
                        value: (
                          <label
                            style={{
                              color: item.type === ProducerReportType.DEBIT ? '#e64f49' : '#3caec8',
                            }}
                          >
                            {toCurrency(item.value)}
                          </label>
                        ),
                      }))}
                    />
                  </div>
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Total:</div>
                        <div className="text-small-black-600">{`${toCurrency(
                          producerReportMain.ticket.summary.discounts,
                        )}`}</div>
                      </div>
                    </>
                  </div>
                </Card>
              )}
            {producerReportMain.product &&
              producerReportMain.product.expenses &&
              producerReportMain.product.expenses.length > 0 && (
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <div className="p-4">
                    <div className="collapseTableText subTitleMain">
                      <label>Bar</label>
                    </div>
                    <CustomTable
                      theme="producerTheme"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={[
                        {
                          name: 'Descrição',
                          selector: row => row.description,
                        },
                        {
                          name: 'Valor base',
                          selector: row => row.baseValue,
                        },
                        {
                          name: 'Taxa',
                          selector: row => row.fee,
                        },
                        {
                          name: 'Valor',
                          selector: row => row.value,
                        },
                      ]}
                      data={producerReportMain.product.expenses.map(item => ({
                        description: item.description,
                        baseValue:
                          item.description === 'Emisao de cortesia' ||
                          item.description === 'Cancelamentos de ingresso' ||
                          item.description === 'Venda em dinheiro ADM/Dashboard' ||
                          item.description === 'Bloqueio de mesa ou cadeira' ||
                          item.description === 'Desbloqueio de mesa ou cadeira' ||
                          item.description === 'Boleto' ||
                          item.description === 'Aluguel equipamento'
                            ? // eslint-disable-next-line radix
                              parseInt(`${item.baseValue}`)
                            : toCurrency(item.baseValue),
                        // eslint-disable-next-line no-nested-ternary
                        fee: item.fee
                          ? item.description === 'Emisao de cortesia' ||
                            item.description === 'Cancelamentos de ingresso' ||
                            item.description === 'Venda em dinheiro ADM/Dashboard' ||
                            item.description === 'Bloqueio de mesa ou cadeira' ||
                            item.description === 'Desbloqueio de mesa ou cadeira' ||
                            item.description === 'Boleto' ||
                            item.description === 'Aluguel equipamento'
                            ? toCurrency(item.fee)
                            : `${toPercentage(item.fee)} %`
                          : 'N/A',
                        value: (
                          <label
                            style={{
                              color: item.type === ProducerReportType.DEBIT ? '#e64f49' : '#3caec8',
                            }}
                          >
                            {toCurrency(item.value)}
                          </label>
                        ),
                      }))}
                    />
                  </div>
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Total:</div>
                        <div className="text-small-black-600">{`${toCurrency(
                          producerReportMain.product.summary.discounts,
                        )}`}</div>
                      </div>
                    </>
                  </div>
                </Card>
              )}
            {producerReportMain.general &&
              producerReportMain.general.expenses &&
              producerReportMain.general.expenses.length > 0 && (
                <Card
                  className="mb-5 card-no-border customTableScroll"
                  style={{ border: 'none', overflow: 'auto' }}
                >
                  <div className="p-4">
                    <div className="collapseTableText subTitleMain">
                      <label>Geral</label>
                    </div>
                    <CustomTable
                      theme="producerTheme"
                      numberRowsPerPage={0}
                      progressPending={false}
                      columns={[
                        {
                          name: 'Descrição',
                          selector: row => row.description,
                        },
                        {
                          name: 'Valor base',
                          selector: row => row.baseValue,
                        },
                        {
                          name: 'Taxa',
                          selector: row => row.fee,
                        },
                        {
                          name: 'Valor',
                          selector: row => row.value,
                        },
                      ]}
                      data={producerReportMain.general.expenses.map(item => ({
                        description: item.description,
                        baseValue:
                          item.description === 'Boleto' ||
                          item.description === 'Aluguel equipamento'
                            ? // eslint-disable-next-line radix
                              parseInt(`${item.baseValue}`)
                            : toCurrency(item.baseValue),
                        // eslint-disable-next-line no-nested-ternary
                        fee: item.fee
                          ? item.description === 'Boleto' ||
                            item.description === 'Aluguel equipamento'
                            ? toCurrency(item.fee)
                            : `${toPercentage(item.fee)} %`
                          : 'N/A',
                        value: (
                          <label
                            style={{
                              color: item.type === ProducerReportType.DEBIT ? '#e64f49' : '#3caec8',
                            }}
                          >
                            {toCurrency(item.value)}
                          </label>
                        ),
                      }))}
                    />
                  </div>
                  <hr className="mb-0" style={{ minWidth: '1080px' }} />
                  <div
                    style={{
                      padding: '30px 40px',
                      gap: '40px',
                    }}
                    className="d-flex justify-content-center"
                  >
                    <>
                      <div className="d-flex">
                        <div className="mr-1 text-small-gray-regular">Total:</div>
                        <div className="text-small-black-600">{`${toCurrency(
                          producerReportMain.general.summary.discounts,
                        )}`}</div>
                      </div>
                    </>
                  </div>
                </Card>
              )}
            {((producerReportMain.waitingTransfers &&
              producerReportMain.waitingTransfers.length > 0) ||
              (producerReportMain.transferredTransfers &&
                producerReportMain.transferredTransfers.length > 0) ||
              (producerReportMain.refusedTransfers &&
                producerReportMain.refusedTransfers.length > 0)) && (
              <Fragment>
                <hr className="dividerUp" />
                <h5>Resgates</h5>
                {producerReportMain.waitingTransfers &&
                  producerReportMain.waitingTransfers.length > 0 && (
                    <Card
                      className="mb-5 card-no-border customTableScroll"
                      style={{ border: 'none', overflow: 'auto' }}
                    >
                      <div className="p-4">
                        <div className="collapseTableText subTitleMain">
                          <label>Aguardando</label>
                        </div>
                        <CustomTable
                          columns={transferColumns}
                          data={dataTableTransfers}
                          numberRowsPerPage={1000}
                          progressPending={state === States.loading}
                          theme="producerTheme"
                        />
                      </div>
                    </Card>
                  )}
                {producerReportMain.transferredTransfers &&
                  producerReportMain.transferredTransfers.length > 0 && (
                    <Card
                      className="mb-5 card-no-border customTableScroll"
                      style={{ border: 'none', overflow: 'auto' }}
                    >
                      <div className="p-4">
                        <div className="collapseTableText subTitleMain">
                          <label>Transferido</label>
                        </div>
                        <CustomTable
                          columns={transferColumns}
                          data={dataTableTransferredTransfers}
                          numberRowsPerPage={1000}
                          progressPending={state === States.loading}
                          theme="producerTheme"
                        />
                      </div>
                    </Card>
                  )}
                {producerReportMain.refusedTransfers &&
                  producerReportMain.refusedTransfers.length > 0 && (
                    <Card
                      className="mb-5 card-no-border customTableScroll"
                      style={{ border: 'none', overflow: 'auto' }}
                    >
                      <div className="p-4">
                        <div className="collapseTableText subTitleMain">
                          <label>Recusados</label>
                        </div>
                        <CustomTable
                          columns={transferColumns}
                          data={dataTablerefRefusedTransfers}
                          numberRowsPerPage={1000}
                          progressPending={state === States.loading}
                          theme="producerTheme"
                        />
                      </div>
                    </Card>
                  )}
              </Fragment>
            )}
          </Fragment>
        )}
      </Container>
    </Fragment>
  );
};
